import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useKeenSlider } from "keen-slider/react";
import { useRef } from "react";
import { useTranslations } from "use-intl";
import "keen-slider/keen-slider.min.css";

import { rudderAnalytics, TripTrackingEvents } from "@/app/lib/rudderstack";
import { TripCarouselCard } from "@/features/trips/components/TripCarouselCard";
import { tripsQueryOptions } from "@/features/trips/hooks/useTrips";
import { useInViewportOnce } from "@/hooks/useInViewportOnce";
import { useLocaleInfo } from "@/hooks/useLocaleInfo";

export function TripCarousel() {
  const t = useTranslations();
  const navigate = useNavigate();
  const { dir } = useLocaleInfo();
  const containerRef = useRef<HTMLDivElement>(null);

  const [sliderRef] = useKeenSlider<HTMLUListElement>({
    loop: true,
    slides: {
      perView: "auto",
    },
    renderMode: "performance",
    rtl: dir === "rtl",
  });
  const { data } = useQuery(tripsQueryOptions());

  useInViewportOnce(containerRef, {
    callback: () => {
      rudderAnalytics.track(TripTrackingEvents.TRIP_CAROUSEL_VIEWED);
    },
  });

  if (!data || !data.length) return null;

  return (
    <div ref={containerRef}>
      <h6 className="mb-4 px-4 text-xl font-bold text-text-title">
        {t("trips.carouselTitle")}
      </h6>

      <ul ref={sliderRef} className="keen-slider">
        {/* keen slider does not duplicate carousel items in loop mode, so we have to duplicate them manually
          to always display items on both sides */}
        {(data.length < 3 ? [...data, ...data] : data).map((trip, index) => (
          <li
            key={`${trip.id}-${index}`}
            className="keen-slider__slide flex w-69 min-w-69 flex-col items-start ps-4 *:grow"
          >
            <TripCarouselCard
              trip={trip}
              onClick={() => {
                rudderAnalytics.track(
                  TripTrackingEvents.TRIP_CAROUSEL_CLICKED,
                  {
                    trip_id: trip.id,
                    trip_name: trip.name,
                  },
                );

                navigate({
                  to: "/trips/$tripId",
                  params: { tripId: trip.id },
                });
              }}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
